import { partition, uniq } from "lodash-es";

import type {
  DictsysGermanAdjectiveInflectionScheme,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanAdjectiveCase,
  DictsysInflectionSchemeGermanAdjectiveCases,
  DictsysInflectionSchemeGermanAdjectiveComparative,
  DictsysInflectionSchemeGermanAdjectiveDegree,
  DictsysInflectionSchemeGermanAdjectiveSuperlative,
  DictsysInflectionSchemeHeaderElement,
  DictsysInflectionSchemeTable
} from "@/types";

import { DictsysInflectionSchemeHelper } from ".";

function getComparativeHeaderText(
  comparative?: DictsysInflectionSchemeGermanAdjectiveComparative
): DictsysInflectionSchemeHeaderElement | null {
  if (!comparative) {
    return null;
  }

  const texts = [comparative[0], comparative[1]]
    .map((content) => content?.index)
    .filter((index): index is string => Boolean(index));

  return { type: "inflection", texts, tense: "komparativ" };
}

function getSuperlativeHeaderTexts(
  superlative?: DictsysInflectionSchemeGermanAdjectiveSuperlative
): DictsysInflectionSchemeHeaderElement[] {
  if (!superlative) {
    return [];
  }

  const forms = ["der", "die", "das"];
  const superlatives = [
    superlative[0],
    superlative[1],
    superlative[2],
    superlative[3],
    superlative[4],
    superlative[5],
    superlative[6],
    superlative[7]
  ].filter((content): content is DictsysInflectionSchemeContent => Boolean(content));

  const [superlativesWithForm, superlativesWithoutForm] = partition(superlatives, (content) =>
    forms.includes(content.prefix)
  );

  const textsWithForm = uniq(superlativesWithForm.map((content) => content.inflexion));

  const textsWithoutForm = superlativesWithoutForm.map((content, index) =>
    index === 0 && content.prefix ? `${content.prefix} ${content.inflexion}` : content.inflexion
  );

  return [
    { type: "inflection", texts: textsWithoutForm, tense: "superlativ" },
    { type: "inflection", prefixes: forms, texts: textsWithForm, tense: "superlativ" }
  ];
}

export function getHeaderElements(
  inflectionScheme: DictsysGermanAdjectiveInflectionScheme
): DictsysInflectionSchemeHeaderElement[] {
  if (!inflectionScheme.adjective) {
    return [];
  }

  const elements: DictsysInflectionSchemeHeaderElement[] = [
    { type: "headWord", texts: [inflectionScheme.adjective.positive[0].index], tense: "positiv" }
  ];

  const comparativeHeaderText = getComparativeHeaderText(inflectionScheme.adjective.comparative);
  if (comparativeHeaderText) {
    elements.push(comparativeHeaderText);
  }

  const superlativeHeaderTexts = getSuperlativeHeaderTexts(inflectionScheme.adjective.superlative);
  if (superlativeHeaderTexts) {
    superlativeHeaderTexts.forEach((superlativeHeaderText) => {
      elements.push(superlativeHeaderText);
    });
  }

  return elements;
}

function getRow(
  header: string,
  adjectiveCase: DictsysInflectionSchemeGermanAdjectiveCase
): DictsysInflectionSchemeCell[] {
  return [
    { type: "sub-header", elements: [{ texts: [header] }] },
    DictsysInflectionSchemeHelper.getBodyCell(adjectiveCase.singular.masculine),
    DictsysInflectionSchemeHelper.getBodyCell(adjectiveCase.singular.feminine),
    DictsysInflectionSchemeHelper.getBodyCell(adjectiveCase.singular.neutral),
    DictsysInflectionSchemeHelper.getBodyCell(adjectiveCase.plural)
  ];
}

function getTable(
  header: string,
  comment: string,
  cases: DictsysInflectionSchemeGermanAdjectiveCases
): DictsysInflectionSchemeTable {
  return {
    header,
    comment,
    cells: [
      [
        { type: "sub-header", elements: [] },
        { type: "sub-header", elements: [{ texts: ["Maskulinum"] }] },
        { type: "sub-header", elements: [{ texts: ["Femininum"] }] },
        { type: "sub-header", elements: [{ texts: ["Neutrum"] }] },
        { type: "sub-header", elements: [{ texts: ["Pluralis (flertal)"] }] }
      ],
      getRow("Nominativ", cases.nominative),
      getRow("Akkusativ", cases.akkusative),
      getRow("Genitiv", cases.genitive),
      getRow("Dativ", cases.dative)
    ]
  };
}

function getTables(degree: DictsysInflectionSchemeGermanAdjectiveDegree): DictsysInflectionSchemeTable[] {
  return [
    getTable("Den stærke bøjning", "(uden artikel)", degree.strong),
    getTable("Den svage bøjning", "(med bestemt artikel)", degree.weak),
    getTable("Den blandede bøjning", "(med ein, kein, mein mv.)", degree.mixed)
  ];
}

export function getPositiveTables(
  inflectionScheme: DictsysGermanAdjectiveInflectionScheme
): DictsysInflectionSchemeTable[] {
  return inflectionScheme.adjective ? getTables(inflectionScheme.adjective.positive) : [];
}

export function getComparativeTables(
  inflectionScheme: DictsysGermanAdjectiveInflectionScheme
): DictsysInflectionSchemeTable[] {
  return inflectionScheme.adjective?.comparative ? getTables(inflectionScheme.adjective.comparative) : [];
}

export function getSuperlativeTables(
  inflectionScheme: DictsysGermanAdjectiveInflectionScheme
): DictsysInflectionSchemeTable[] {
  return inflectionScheme.adjective?.superlative ? getTables(inflectionScheme.adjective.superlative) : [];
}
